/**
 * Admin audit-log list (PR-6a — operational observability).
 *
 * SUPER-only — the backend gates this surface with IsSuperAdmin. TENANT
 * operators get 403 from the API; we hide the link in the sidebar but
 * still render a graceful error if they deep-link here.
 *
 * No detail endpoint — every field the operator needs is on the row.
 * Click a row to expand the metadata JSON inline.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ScrollText, RotateCw } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DataTable, type DataTableColumn } from "@/components/shell/DataTable";
import { EmptyState } from "@/components/shell/EmptyState";
import { PageHeader } from "@/components/shell/PageHeader";
import { SearchInput } from "@/components/shell/SearchInput";
import { Toolbar } from "@/components/shell/Toolbar";
import { JsonBlock } from "@/components/payments/JsonBlock";
import { getAuditLog, type AuditLogRow } from "@/lib/api";

export function AuditLogListPage() {
  const [params, setParams] = useSearchParams();

  const q = params.get("q") ?? undefined;

  const [searchInput, setSearchInput] = useState(q ?? "");
  useEffect(() => {
    const t = setTimeout(() => {
      const next = new URLSearchParams(params);
      if (searchInput) next.set("q", searchInput);
      else next.delete("q");
      setParams(next, { replace: true });
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const query = useQuery({
    queryKey: ["audit-log", { q }] as const,
    queryFn: () => getAuditLog({ q }),
  });

  useEffect(() => {
    if (query.error) {
      toast.error("Couldn't load audit log", {
        description: "Audit log is SUPER-only — check that you're signed in as a super admin.",
      });
    }
  }, [query.error]);

  const [expandedId, setExpandedId] = useState<string | null>(null);

  const hasFilters = Boolean(q);

  const columns = useMemo<ReadonlyArray<DataTableColumn<AuditLogRow>>>(
    () => [
      {
        key: "created_at",
        header: "When",
        className: "text-muted-foreground",
        cell: (r) => (
          <span className="whitespace-nowrap text-xs">
            {new Date(r.created_at).toLocaleString()}
          </span>
        ),
      },
      {
        key: "actor",
        header: "Actor",
        cell: (r) =>
          r.actor ? (
            <div className="flex min-w-0 flex-col">
              <span className="truncate text-sm">{r.actor.email}</span>
              <span className="text-xs uppercase text-muted-foreground">
                {r.actor.role}
              </span>
            </div>
          ) : (
            <Badge variant="muted">system</Badge>
          ),
      },
      {
        key: "action",
        header: "Action",
        cell: (r) => <code className="font-mono text-xs">{r.action}</code>,
      },
      {
        key: "target",
        header: "Target",
        cell: (r) => (
          <div className="flex min-w-0 flex-col">
            {r.target_type ? (
              <span className="text-xs uppercase text-muted-foreground">
                {r.target_type}
              </span>
            ) : null}
            {r.target_id ? (
              <code className="truncate font-mono text-xs">{r.target_id}</code>
            ) : (
              <span className="text-xs text-muted-foreground">—</span>
            )}
          </div>
        ),
      },
      {
        key: "request_ip",
        header: "IP",
        cell: (r) => (
          <code className="font-mono text-xs text-muted-foreground">
            {r.request_ip ?? "—"}
          </code>
        ),
      },
    ],
    [],
  );

  const rows = query.data?.results ?? [];

  return (
    <>
      <PageHeader
        eyebrow="Operations / Forensics"
        title="Audit log"
        description="Append-only record of every state-changing operation across the admin portal."
        action={
          <Button
            variant="outline"
            size="sm"
            onClick={() => query.refetch()}
            disabled={query.isFetching}
          >
            <RotateCw className={query.isFetching ? "animate-spin" : undefined} />
            Refresh
          </Button>
        }
      />

      <Toolbar
        search={
          <SearchInput
            placeholder="Search by action, target ID, or actor email…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onClear={() => setSearchInput("")}
          />
        }
      />

      <DataTable<AuditLogRow>
        columns={columns}
        rows={rows}
        loading={query.isLoading}
        rowKey={(r) => r.id}
        selectedKey={expandedId ?? undefined}
        onRowClick={(r) => setExpandedId(expandedId === r.id ? null : r.id)}
        emptyState={
          <EmptyState
            icon={ScrollText}
            title="No audit rows match these filters"
            description="Try clearing the filter. Rows appear here as operators perform state-changing actions."
            action={
              hasFilters ? (
                <Button variant="outline" size="sm" asChild>
                  <Link to="/audit-log">Clear filters</Link>
                </Button>
              ) : undefined
            }
          />
        }
      />

      {expandedId
        ? (() => {
            const row = rows.find((r) => r.id === expandedId);
            if (!row) return null;
            return (
              <div className="mt-4 rounded-md border bg-card p-4">
                <div className="mb-2 flex items-baseline justify-between gap-2">
                  <h3 className="text-sm font-medium">Metadata</h3>
                  <code className="font-mono text-xs text-muted-foreground">
                    {row.id}
                  </code>
                </div>
                <JsonBlock value={row.metadata} />
              </div>
            );
          })()
        : null}
    </>
  );
}

export default AuditLogListPage;
